import type { KnowledgeScopeOption, LanguageModeOption, ToneLevelOption } from '../api'

export type NormalizedLanguageModeOption = Extract<LanguageModeOption, 'modern' | 'period'>

export type PreferenceOption<T extends string> = {
  value: T
  label: string
  description: string
}

export const LANGUAGE_MODE_OPTIONS: PreferenceOption<LanguageModeOption>[] = [
  {
    value: 'modern',
    label: 'Modern',
    description: 'Uses contemporary language for clarity while keeping historical details intact.',
  },
  {
    value: 'period',
    label: 'Period',
    description: 'Attempts to mirror the speech patterns of the era the figure lived in.',
  },
]

export const KNOWLEDGE_SCOPE_OPTIONS: PreferenceOption<KnowledgeScopeOption>[] = [
  {
    value: 'historical_only',
    label: 'Historical only',
    description: 'Responses stick to information the figure could reasonably know in their lifetime.',
  },
  {
    value: 'bounded',
    label: 'Bounded',
    description: 'Allows limited curator-provided supplemental context beyond the figure\'s lived experience.',
  },
  {
    value: 'full_context',
    label: 'Full context',
    description:
      'Unlocks modern scholarship and recent discoveries so the figure can reference contemporary knowledge.',
  },
]

export const TONE_LEVEL_OPTIONS: PreferenceOption<ToneLevelOption>[] = [
  {
    value: 'child',
    label: 'Child-friendly',
    description: 'Gentle tone with simplified explanations and additional guardrails.',
  },
  {
    value: 'teen',
    label: 'Teen',
    description: 'Balances levity and nuance, ideal for classroom-style sessions.',
  },
  {
    value: 'adult',
    label: 'Adult',
    description: 'Full nuance, references, and candid voice suitable for expert audiences.',
  },
]

export const normalizeLanguageMode = (value: LanguageModeOption | null | undefined): NormalizedLanguageModeOption => {
  if (value === 'period' || value === 'period_authentic') return 'period'
  return 'modern'
}

export const getLanguageModeLabel = (value: LanguageModeOption | null | undefined): string => {
  const normalized = normalizeLanguageMode(value)
  return LANGUAGE_MODE_OPTIONS.find((option) => option.value === normalized)?.label ?? 'Modern'
}

export const getKnowledgeScopeLabel = (value: KnowledgeScopeOption | null | undefined): string => {
  return KNOWLEDGE_SCOPE_OPTIONS.find((option) => option.value === value)?.label ?? 'Bounded'
}

export const getToneLevelLabel = (value: ToneLevelOption | null | undefined): string => {
  return TONE_LEVEL_OPTIONS.find((option) => option.value === value)?.label ?? 'Adult'
}
